import Autocomplete from "@/components/Autocomplete";
import { ADDRESS_FRAGMENT } from "@/fragments";
import { formattedAddress } from "@/lib/addressHelpers";
import { Address } from "@/types";
import { TypedDocumentNode, gql, useQuery } from "@apollo/client";
import { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";

type FacilityAutocompleteResult = {
  id: string;
  name: string;
  mainAddress: Address | null;
};

type FacilitiesSearchQueryResponse = {
  facilities: { nodes: FacilityAutocompleteResult[] };
};

export const FACILITY_AUTOCOMPLETE_QUERY: TypedDocumentNode<
  FacilitiesSearchQueryResponse,
  { input?: { search: string } }
> = gql`
  query facilityAutocompleteQuery($input: BackofficeCRMFacilitiesInput!) {
    facilities(first: 10, input: $input) {
      nodes {
        id
        name
        mainAddress {
          ...AddressFragment
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

function FacilityAutocomplete({
  name,
  label,
  initialQuery,
  required,
  excludeIds = []
}: {
  name: string;
  label: string;
  initialQuery: string;
  required?: boolean;
  excludeIds?: string[];
}) {
  const {
    control,
    formState: { errors, isSubmitting }
  } = useFormContext();
  const [query, setQuery] = useState(initialQuery);
  const { data, loading } = useQuery(FACILITY_AUTOCOMPLETE_QUERY, {
    variables: { input: { search: query } }
  });
  const facilities = (data?.facilities.nodes || []).filter((facility) => !excludeIds.includes(facility.id));
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => (
        <Autocomplete<FacilityAutocompleteResult>
          variant="searchableDropdown"
          data={facilities}
          loadingResults={loading}
          query={query}
          label={label}
          renderItem={(opt) => (
            <div className="flex flex-col">
              <div className="text-body-large">{opt.name}</div>
              {opt.mainAddress != null ? (
                <div className="text-label-medium text-sys-brand-on-surface-variant">
                  {formattedAddress(opt.mainAddress)}
                </div>
              ) : null}
            </div>
          )}
          reset={() => {
            onChange("");
            setQuery("");
          }}
          onChangeText={setQuery}
          onSelect={(value) => {
            onChange(value.id);
            setQuery(value.name);
          }}
          disabled={isSubmitting}
          required={required}
          error={!!errors[name]}
          errorText={errors[name]?.message as string}
          constrainMenuWidth
          menuPositioning="fixed"
        />
      )}
    />
  );
}

export default FacilityAutocomplete;
